import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueries, useQuery } from '@tanstack/react-query'

import { cn } from '@/lib/utils'
import { LocationsApi } from '@/services/targeting/targetingLookupApi'
import type { NamedLocationRef } from '@/services/targeting/targeting'
import { PickerColumn, type PickerColumnRow } from '../PickerColumn'

/** A postcode holds nothing, so it is the one row that cannot be opened. */
const LEAF_TYPES = ['POSTCODE', 'POSTAL_CODE']

export interface LocationBrowserProps {
  /** The locations currently targeted — the ticked rows. */
  selected: NamedLocationRef[]
  onToggle: (location: NamedLocationRef, selected: boolean) => void
  className?: string
}

/**
 * Walks the location tree as cascading columns — continents, then a continent's
 * countries, a country's regions, and so on down to postcodes — in the same
 * shape as the inventory browser.
 *
 * Opening and targeting are separate here, unlike the inventory columns: a
 * country is walked into by clicking its name and targeted by ticking its box,
 * so reaching a city does not target the country it sits in.
 */
export function LocationBrowser({ selected, onToggle, className }: LocationBrowserProps) {
  const { t } = useTranslation()
  /** The opened row of each column, widest first — the trail into the tree. */
  const [path, setPath] = useState<NamedLocationRef[]>([])
  const scroller = useRef<HTMLDivElement>(null)

  const roots = useQuery({
    queryKey: ['targeting-location-roots'],
    queryFn: () => LocationsApi.roots(),
    staleTime: 60 * 60 * 1000,
  })

  const children = useQueries({
    queries: path.map((node) => ({
      queryKey: ['targeting-location-children', node.id],
      queryFn: () => LocationsApi.children(node.id),
      staleTime: 60 * 60 * 1000,
    })),
  })

  // A newly opened column is off to the right on a narrow screen, so the strip
  // follows the walk rather than leaving it to be found.
  useEffect(() => {
    const element = scroller.current
    if (element) element.scrollLeft = element.scrollWidth
  }, [path.length])

  /**
   * A location type's own name — plural for a column heading, singular for the
   * badge on a row. An unknown type falls back to the generic "Locations".
   */
  const locationTypeLabel = (type: string | undefined, plural = false) => {
    if (!type) return plural ? t('targeting.types.namedLocation.plural') : ''
    const singular = t(`targeting.location.types.${type}`, { defaultValue: type })
    if (!plural) return singular
    return t(`targeting.location.typePlural.${type}`, { defaultValue: singular })
  }

  const selectedIds = useMemo(() => selected.map((location) => String(location.id)), [selected])

  const columns = [
    {
      page: roots.data,
      loading: roots.isLoading,
      parent: undefined as NamedLocationRef | undefined,
    },
    ...children.map((query, index) => ({
      page: query.data,
      loading: query.isLoading,
      parent: path[index],
    })),
  ]

  return (
    <div ref={scroller} className={cn('flex h-96 overflow-x-auto rounded-md border', className)}>
      {columns.map((column, depth) => {
        const locations = column.page?.results ?? []
        const byId = new Map(locations.map((location) => [String(location.id), location]))
        const rows: PickerColumnRow[] = locations.map((location) => ({
          id: String(location.id),
          name: location.name ?? String(location.id),
          sub: depth === 0 ? undefined : location.context,
          meta: locationTypeLabel(location.type),
          openable: !LEAF_TYPES.includes(location.type ?? ''),
        }))
        // Every column is named after what it holds — the levels differ from
        // country to country, so the type of the rows is the only honest label.
        const label = column.parent
          ? `${column.parent.name} — ${locationTypeLabel(locations[0]?.type, true)}`
          : t('targeting.location.continents')
        const notShown = (column.page?.totalHits ?? 0) - locations.length

        return (
          <div
            key={column.parent?.id ?? 'roots'}
            className="flex h-full w-70 shrink-0 grow basis-70 flex-col border-r last:border-r-0"
          >
            <PickerColumn
              label={label}
              rows={rows}
              selectedIds={selectedIds}
              onToggle={(id, isSelected) => {
                const location = byId.get(id)
                if (location) onToggle(location, isSelected)
              }}
              onOpen={(id) => {
                const location = byId.get(id)
                if (!location) return
                // Opening a row replaces whatever was open to its right.
                setPath([...path.slice(0, depth), location])
              }}
              openId={path[depth] ? String(path[depth].id) : undefined}
              searchPlaceholder={t('targeting.location.filterColumn')}
              loading={column.loading}
              emptyText={
                column.parent
                  ? t('targeting.location.noneInside', { name: column.parent.name })
                  : t('targeting.location.noContinents')
              }
              footer={
                notShown > 0 ? (
                  <p className="p-3 text-xs text-muted-foreground">
                    {t('targeting.location.notShown', { count: notShown })}
                  </p>
                ) : undefined
              }
            />
          </div>
        )
      })}
    </div>
  )
}
